import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  FileText, Sparkles, CheckCircle, AlertCircle, Loader2,
  PenTool, Target, Lightbulb, ArrowRight, Coins, MessageSquare,
  ShieldAlert, ShieldCheck, AlertTriangle
} from 'lucide-react';
import { AI_PERSONAS } from '../constants/aiPersonas';

const EssayReview = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [essay, setEssay] = useState('');
  const [essayType, setEssayType] = useState('personal_statement');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [credits, setCredits] = useState(null);
  const creditCost = 3;

  const essayTypes = [
    { value: 'personal_statement', label: 'Personal Statement / Common App Essay' },
    { value: 'supplemental', label: 'Supplemental Essay' },
    { value: 'why_us', label: '"Why This College" Essay' },
    { value: 'diversity', label: 'Diversity Essay' },
    { value: 'extracurricular', label: 'Extracurricular Activity Essay' },
    { value: 'challenge', label: 'Challenge/Obstacle Essay' }
  ];

  // Check credits on mount
  React.useEffect(() => {
    if (user) {
      checkCredits();
    }
  }, [user]);

  const checkCredits = async () => {
    try {
      const response = await api.get('/subscription/usage');
      setCredits(response.data.credits);
    } catch (error) {
      console.error('Failed to check credits:', error);
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      toast({
        title: 'Login Required',
        description: 'Please log in to use Essay Review',
        variant: 'destructive'
      });
      navigate('/login');
      return;
    }

    if (!essay.trim()) {
      toast({
        title: 'Essay Required',
        description: 'Please paste your essay to get feedback',
        variant: 'destructive'
      });
      return;
    }

    if (essay.trim().split(/\s+/).length < 50) {
      toast({
        title: 'Essay Too Short',
        description: 'Please provide at least 50 words for meaningful feedback',
        variant: 'destructive'
      });
      return;
    }

    if (credits !== null && credits < creditCost) {
      toast({
        title: 'Insufficient Credits',
        description: `You need ${creditCost} credits for essay review. You have ${credits}.`,
        variant: 'destructive'
      });
      navigate('/pricing');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/ai/essay-review', {
        essay: essay.trim(),
        essay_type: essayType
      });

      setFeedback(response.data);
      setCredits(prev => prev - creditCost);
      
      toast({
        title: 'Review Complete!',
        description: 'Your essay feedback is ready',
      });
    } catch (error) {
      console.error('Essay review failed:', error);
      toast({
        title: 'Review Failed',
        description: error.response?.data?.detail || 'Failed to analyze essay',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const wordCount = essay.trim() ? essay.trim().split(/\s+/).length : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 overflow-hidden border-4 border-indigo-200 shadow-lg">
            <img 
              src={AI_PERSONAS.WASIF.avatar} 
              alt="Wasif - Essay Assistant"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Meet Wasif - Essay Assistant</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get AI-powered feedback on your essays with authenticity detection
          </p>
          <p className="text-sm text-amber-600 mt-2 flex items-center justify-center gap-2">
            <ShieldAlert className="h-4 w-4" />
            Includes AI-written content detection to help you write authentically
          </p>
          <div className="flex justify-center gap-3 mt-4">
            <Badge className="bg-indigo-100 text-indigo-700">
              <Coins className="h-3 w-3 mr-1" /> {creditCost} Credits
            </Badge>
            {credits !== null && (
              <Badge variant="outline">
                Your Balance: {credits} credits
              </Badge>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-indigo-600" />
                  Your Essay
                </CardTitle>
                <CardDescription>
                  Paste your essay below for AI-powered feedback
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Essay Type</Label>
                  <Select value={essayType} onValueChange={setEssayType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {essayTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <Label>Essay Content</Label>
                    <span className={`text-sm ${wordCount < 50 ? 'text-amber-600' : wordCount > 650 ? 'text-red-600' : 'text-gray-500'}`}>
                      {wordCount} words
                    </span>
                  </div>
                  <Textarea
                    value={essay}
                    onChange={(e) => setEssay(e.target.value)}
                    placeholder="Paste your essay here... (minimum 50 words)"
                    className="min-h-[300px] resize-none"
                  />
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={loading || !essay.trim()}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Get Feedback ({creditCost} credits)
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Tips Card */}
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="p-4">
                <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
                  <Lightbulb className="h-4 w-4" />
                  Essay Tips
                </h3>
                <ul className="space-y-2 text-sm text-amber-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    Be authentic - admissions officers want to hear YOUR voice
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    Show, don't tell - use specific examples and stories
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    Stay within word limits (usually 250-650 words)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    Proofread multiple times before submitting
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Feedback Section */}
          <div>
            {feedback ? (
              <Card className="sticky top-4">
                <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    AI Feedback
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {/* AI Detection Alert */}
                  {feedback.ai_detection && (
                    <div className={`p-4 rounded-lg border-2 ${
                      feedback.ai_detection.likelihood === 'high' 
                        ? 'bg-red-50 border-red-300' 
                        : feedback.ai_detection.likelihood === 'medium'
                        ? 'bg-amber-50 border-amber-300'
                        : 'bg-green-50 border-green-300'
                    }`}>
                      <div className="flex items-start gap-3">
                        {feedback.ai_detection.likelihood === 'high' ? (
                          <ShieldAlert className="h-6 w-6 text-red-600 flex-shrink-0" />
                        ) : feedback.ai_detection.likelihood === 'medium' ? (
                          <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0" />
                        ) : (
                          <ShieldCheck className="h-6 w-6 text-green-600 flex-shrink-0" />
                        )}
                        <div>
                          <h4 className={`font-semibold ${
                            feedback.ai_detection.likelihood === 'high' 
                              ? 'text-red-800' 
                              : feedback.ai_detection.likelihood === 'medium'
                              ? 'text-amber-800'
                              : 'text-green-800'
                          }`}>
                            Authenticity Check: {feedback.ai_detection.likelihood === 'high' 
                              ? 'Likely AI-Generated' 
                              : feedback.ai_detection.likelihood === 'medium'
                              ? 'Some AI Patterns Detected'
                              : 'Appears Human-Written'}
                          </h4>
                          <p className={`text-sm mt-1 ${
                            feedback.ai_detection.likelihood === 'high' 
                              ? 'text-red-700' 
                              : feedback.ai_detection.likelihood === 'medium'
                              ? 'text-amber-700'
                              : 'text-green-700'
                          }`}>
                            {feedback.ai_detection.explanation}
                          </p>
                          {feedback.ai_detection.likelihood !== 'low' && (
                            <div className="mt-3 p-2 bg-white/50 rounded text-sm">
                              <p className="font-medium text-gray-700">Why this matters:</p>
                              <p className="text-gray-600">Colleges value authentic student voices. Essays flagged as AI-written may be rejected or investigated. Rewrite in your own words using your personal experiences.</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span>Human-written</span>
                          <span>AI-generated</span>
                        </div>
                        <Progress 
                          value={feedback.ai_detection.ai_probability || 0} 
                          className={`h-2 ${
                            feedback.ai_detection.likelihood === 'high' 
                              ? '[&>div]:bg-red-500' 
                              : feedback.ai_detection.likelihood === 'medium'
                              ? '[&>div]:bg-amber-500'
                              : '[&>div]:bg-green-500'
                          }`}
                        />
                        <p className="text-xs text-center mt-1 text-gray-500">
                          {feedback.ai_detection.ai_probability || 0}% AI probability
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Overall Score */}
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Overall Score</p>
                    <p className="text-4xl font-bold text-indigo-600">
                      {feedback.score || 85}/100
                    </p>
                  </div>

                  {/* Strengths */}
                  <div>
                    <h4 className="font-semibold text-green-700 flex items-center gap-2 mb-3">
                      <CheckCircle className="h-4 w-4" />
                      Strengths
                    </h4>
                    <ul className="space-y-2">
                      {(feedback.strengths || ['Strong personal voice', 'Good use of specific examples', 'Clear narrative structure']).map((strength, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className="text-green-500">+</span>
                          {strength}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Areas for Improvement */}
                  <div>
                    <h4 className="font-semibold text-amber-700 flex items-center gap-2 mb-3">
                      <Target className="h-4 w-4" />
                      Areas for Improvement
                    </h4>
                    <ul className="space-y-2">
                      {(feedback.improvements || ['Consider adding more specific details', 'Strengthen the conclusion', 'Vary sentence structure']).map((item, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className="text-amber-500">!</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Detailed Feedback */}
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3">Detailed Analysis</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {feedback.detailed_feedback || 'Your essay demonstrates a clear understanding of the prompt and presents your experiences effectively. The personal anecdotes help create an authentic voice. Consider strengthening your conclusion to leave a lasting impression on the reader.'}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setFeedback(null);
                        setEssay('');
                      }}
                    >
                      Review Another Essay
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="sticky top-4">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <MessageSquare className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="font-semibold text-gray-700 mb-2">Feedback Will Appear Here</h3>
                  <p className="text-sm text-gray-500">
                    Paste your essay and click "Get Feedback" to receive AI-powered analysis
                  </p>

                  <div className="mt-8 text-left space-y-4">
                    <h4 className="font-semibold text-gray-700">What You'll Get:</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Target className="h-4 w-4 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-700">Overall Score</p>
                          <p className="text-sm text-gray-500">Get a score out of 100</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-700">Strengths Analysis</p>
                          <p className="text-sm text-gray-500">What's working well in your essay</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Lightbulb className="h-4 w-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-700">Improvement Suggestions</p>
                          <p className="text-sm text-gray-500">Actionable tips to make it better</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EssayReview;
